import { useState, useEffect } from 'react';
import api from '../../services/api';

const SEED_ITEMS = [
	{
		key: 'manufacturerSettings',
		label: 'Manufacturer Quote Settings',
		description: 'Per-factory trigger configurations'
	},
	{
		key: 'hsCodes',
		label: 'Manufacturer HS Codes',
		description: 'Pre-registered HS codes for testing lookup'
	},
	{
		key: 'fuelPrices',
		label: 'Fuel Prices',
		description: 'Regional diesel prices for all regions'
	},
	{
		key: 'writebackLogs',
		label: 'Write-Back Logs',
		description: 'Sample success/failure logs for dashboard'
	},
	{
		key: 'routes',
		label: 'Cached Routes',
		description: 'Pre-cached Google Maps routes'
	}
];

export default function TestDataSetup() {
	const [status, setStatus] = useState(null);
	const [loading, setLoading] = useState(false);
	const [seeding, setSeeding] = useState(false);
	const [result, setResult] = useState(null);
	const [error, setError] = useState(null);

	useEffect(() => {
		checkStatus();
	}, []);

	const checkStatus = async () => {
		try {
			setLoading(true);
			setError(null);
			const response = await api.get('/api/freight/seed-status');
			// response is already unwrapped by axios interceptor (response.data)
			// backend returns { success, data: { isSeeded, counts } }
			setStatus(response.data || response);
		} catch (err) {
			setError('Failed to check seed status: ' + (err.message || 'Unknown error'));
		} finally {
			setLoading(false);
		}
	};

	const handleSeed = async () => {
		try {
			setSeeding(true);
			setError(null);
			setResult(null);
			const response = await api.post('/api/freight/seed-test-data');
			setResult(response.data);
			// Refresh status after seeding
			await checkStatus();
		} catch (err) {
			setError('Failed to seed test data: ' + (err.message || 'Unknown error'));
		} finally {
			setSeeding(false);
		}
	};

	const isFullySeeded = status?.isSeeded;

	return (
		<div style={{ maxWidth: '800px', margin: '0 auto', padding: '1.5rem' }}>
			<div className="page-header">
				<h1>V3 Test Data Setup</h1>
			</div>

			<p className="text-muted" style={{ marginBottom: '1.5rem' }}>
				This page helps you set up test data for the Freight Estimates V3 features.
				Once seeded, you can test all V3 functionality from the frontend without any backend setup.
			</p>

			{error && (
				<div className="alert alert-error">{error}</div>
			)}

			{result && (
				<div className="alert alert-success">
					Test data seeded successfully! Refresh the page to see updates.
				</div>
			)}

			{/* Seed Status */}
			<div className="form-section">
				<h2>Seed Status</h2>

				{loading ? (
					<div className="loading">Checking status...</div>
				) : status ? (
					<>
						<div className={`alert ${isFullySeeded ? 'alert-success' : 'alert-warning'}`}>
							{isFullySeeded
								? 'All test data is seeded and ready!'
								: 'Test data needs to be seeded'}
						</div>

						<table className="simple-table">
							<thead>
								<tr>
									<th>Data Type</th>
									<th>Records</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>Manufacturer Settings</td>
									<td>{status.counts?.manufacturerSettings ?? 0}</td>
									<td>
										{(status.counts?.manufacturerSettings ?? 0) > 0
											? <span className="badge badge-success">OK</span>
											: <span className="badge badge-danger">Missing</span>}
									</td>
								</tr>
								<tr>
									<td>HS Codes</td>
									<td>{status.counts?.hsCodes ?? 0}</td>
									<td>
										{(status.counts?.hsCodes ?? 0) > 0
											? <span className="badge badge-success">OK</span>
											: <span className="badge badge-danger">Missing</span>}
									</td>
								</tr>
								<tr>
									<td>Fuel Prices</td>
									<td>{status.counts?.fuelPrices ?? 0}</td>
									<td>
										{(status.counts?.fuelPrices ?? 0) > 0
											? <span className="badge badge-success">OK</span>
											: <span className="badge badge-danger">Missing</span>}
									</td>
								</tr>
								<tr>
									<td>Write-Back Logs</td>
									<td>{status.counts?.writebackLogs ?? 0}</td>
									<td>
										{(status.counts?.writebackLogs ?? 0) > 0
											? <span className="badge badge-success">OK</span>
											: <span className="badge badge-danger">Missing</span>}
									</td>
								</tr>
								<tr>
									<td>Providers</td>
									<td>{status.counts?.providers ?? 0}</td>
									<td>
										{(status.counts?.providers ?? 0) > 0
											? <span className="badge badge-success">OK</span>
											: <span className="badge badge-danger">Missing</span>}
									</td>
								</tr>
							</tbody>
						</table>
					</>
				) : (
					<p className="text-muted">Unable to check status</p>
				)}
			</div>

			{/* Seed Test Data */}
			<div className="form-section">
				<h2>Seed Test Data</h2>

				<p className="text-muted" style={{ marginBottom: '1rem' }}>
					Click the button below to populate the database with test data for all V3 features:
				</p>

				<ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
					{SEED_ITEMS.map((item) => (
						<li key={item.key} style={{ marginBottom: '0.5rem' }}>
							<strong>{item.label}</strong> -- {item.description}
						</li>
					))}
				</ul>

				<div className="form-actions">
					<button
						className="btn btn-primary"
						onClick={handleSeed}
						disabled={seeding || isFullySeeded}
					>
						{seeding ? 'Seeding...' : isFullySeeded ? 'Already Seeded' : 'Seed Test Data'}
					</button>
					<button
						className="btn btn-secondary"
						onClick={checkStatus}
						disabled={loading}
					>
						Refresh Status
					</button>
				</div>
			</div>

			{/* Next Steps */}
			<div className="form-section">
				<h2>Next Steps</h2>

				<p className="text-muted">Once data is seeded, you can test V3 features:</p>
				<ul style={{ paddingLeft: '1.5rem' }}>
					<li><strong>Manufacturer Quote Settings:</strong> Configure per-factory triggers</li>
					<li><strong>HS Code Manager:</strong> Register and lookup HS codes</li>
					<li><strong>Fuel Cost Admin:</strong> View and update fuel prices</li>
					<li><strong>Write-Back Dashboard:</strong> Monitor DB write operations</li>
				</ul>
			</div>
		</div>
	);
}
